/**
 * VehicleResultPage Search, Sort & Filter Management Mixin
 *
 * This mixin is SPECIFIC to VehicleResultPage component only.
 * Provides methods for handling search, sorting, filtering, and pagination.
 *
 * All methods are prefixed with "vrpSearch_" to indicate their source.
 *
 * Methods provided:
 * - vrpSearch_handleSort: Handle sort by selection
 * - vrpSearch_handleSortOrder: Handle sort order (ASC/DESC)
 * - vrpSearch_handleSearch: Handle search submission
 * - vrpSearch_handlePageSize: Handle page size change
 * - vrpSearch_handleSelectRepayment: Handle repayment option selection
 * - vrpSearch_onResetFilters: Reset all filters and fetch default results
 * - vrpSearch_fetchVehicles: Fetch vehicles for current page
 * - vrpSearch_initialFetchRatebook: Initial ratebook fetch on component mount
 * - vrpSearch_fetchUserShortlist: Fetch user's shortlist from server
 * - vrpSearch_calculateTotalPages: Calculate total pages for pagination
 */
import { DEFAULT_LEASE_TERM, RATEBOOK_SORT_WEEK_LEASE_PRICE } from '../../utils/constants';

export default {
  methods: {
    /**
     * Handle sort by selection
     * @param {Object} value - Sort option { label, value }
     */
    async vrpSearch_handleSort(value) {
      this.sortBy = value;
      await this.fetchRatebook(
        {
          ...this.searchOptions,
          sortBy: this.sortBy.value,
          sortOrder: this.sortOrder,
          minDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM,
          maxDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM
        },
        true
      );
      this.totalPages = this.vrpSearch_calculateTotalPages();
      this.currentPage = 1;
    },

    /**
     * Handle sort order change (ASC/DESC)
     * @param {String} value - Sort order ('ASC' or 'DESC')
     */
    async vrpSearch_handleSortOrder(value) {
      this.sortOrder = value;
      await this.fetchRatebook(
        {
          ...this.searchOptions,
          sortBy: this.sortBy.value,
          sortOrder: value,
          minDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM,
          maxDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM
        },
        true
      );
      this.totalPages = this.vrpSearch_calculateTotalPages();
      this.currentPage = 1;
    },

    /**
     * Handle repayment option selection
     * @param {Object} repayment - Repayment option { label, value }
     */
    vrpSearch_handleSelectRepayment(repayment) {
      this.repayment = repayment.value;
    },

    /**
     * Refetch when payment cadence changes while sorted by rental price.
     * @param {string} newValue
     * @param {string} oldValue
     */
    async vrpSearch_onRepaymentChange(newValue, oldValue) {
      if (!oldValue || newValue === oldValue || this.isInitializingLeaseTerm) {
        return;
      }

      if (this.sortBy?.value !== RATEBOOK_SORT_WEEK_LEASE_PRICE) {
        return;
      }

      // Mobile: repayment changes are staged until Update search / refine submit
      if (this.$q?.screen?.lt?.md) {
        return;
      }

      await this.vrpSearch_fetchVehicles();
    },

    /**
     * Fetch vehicles for current page
     * Scrolls to result container and fetches ratebook with current filters
     */
    async vrpSearch_fetchVehicles() {
      // Prevent fetch during initialization to avoid duplicate calls
      if (this.isInitializingLeaseTerm) {
        return;
      }
      if (this.$refs.resultContainer && this.$refs.resultContainer.scrollIntoView) {
        this.$refs.resultContainer.scrollIntoView(true);
      }
      if (!this.initialLoading) {
        await this.fetchRatebook(
          {
            ...this.searchOptions,
            currentPage: this.currentPage,
            pageSize: this.pageSize,
            sortBy: this.sortBy.value,
            sortOrder: this.sortOrder,
            minDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM,
            maxDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM
          },
          true
        );
      }
    },

    /**
     * Handle search submission
     * Resets pagination and closes search modal
     */
    async vrpSearch_handleSearch() {
      this.totalPages = this.vrpSearch_calculateTotalPages();
      this.currentPage = 1;
      this.showRefineSearchModal = false;
    },

    /**
     * Handle page size change
     * Fetches ratebook with new page size and resets to page 1
     */
    async vrpSearch_handlePageSize() {
      await this.fetchRatebook(
        {
          ...this.searchOptions,
          sortBy: this.sortBy.value,
          sortOrder: this.sortOrder,
          minDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM,
          maxDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM
        },
        true
      );
      this.totalPages = this.vrpSearch_calculateTotalPages();
      this.currentPage = 1;
    },

    /**
     * Reset all filters and fetch default results
     * Clears URL params, resets refine search UIs, and fetches default ratebook
     */
    async vrpSearch_onResetFilters() {
      // 1) Clear URL params first (except type) - this will trigger queryParams watcher
      const { type } = this.$route.query || {};
      await this.$router
        .replace({
          path: this.$route.path,
          query: type ? { type } : {}
        })
        .catch((err) => {
          if (!err || err.name !== 'NavigationDuplicated') throw err;
        });

      await this.$nextTick();

      // 2) Reset local state to defaults (after URL is cleared and watcher has updated)
      // Remove 'type' from searchOptions if it exists (type is not a search filter)
      const resetSearchOptions = { ...this.queryParams };
      delete resetSearchOptions.type;
      this.searchOptions = resetSearchOptions;
      this.currentPage = 1;
      this.repayment = 'weekly';
      this.sortBy = {
        label: 'Make',
        value: 'vehicle_description'
      };
      this.sortOrder = 'ASC';

      await this.$nextTick();

      // 3) Reset all refine search UIs (NO FETCH HERE)
      // Reset desktop refine search
      const refine = this.$refs.refine;
      const list = Array.isArray(refine) ? refine : refine ? [refine] : [];
      list.forEach((c) => c?.resetLocalState?.());

      // Reset mobile filter drawer refine search if it exists
      if (this.$refs.vehicleResultFilterDrawer) {
        this.$refs.vehicleResultFilterDrawer.resetLocalState();
      }

      // Reset vehicle result actions refine search if it exists
      if (this.$refs.vehicleResultActions) {
        this.$refs.vehicleResultActions.resetRefineSearchState();
      }

      // 4) Fetch defaults ONCE with reset state
      this.initialLoading = true;
      this.setAppLoading(true);

      await this.fetchRatebook({}, true);

      this.totalPages = this.vrpSearch_calculateTotalPages();
      this.initialLoading = false;
      this.setAppLoading(false);
    },

    /**
     * Initial ratebook fetch on component mount
     * Fetches ratebook with current search options and sort settings
     */
    async vrpSearch_initialFetchRatebook() {
      this.initialLoading = true;
      await this.fetchRatebook(
        {
          ...this.searchOptions,
          sortBy: this.sortBy.value,
          sortOrder: this.sortOrder,
          minDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM,
          maxDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM
        },
        true
      );
      this.totalPages = this.vrpSearch_calculateTotalPages();
      this.initialLoading = false;
      this.setAppLoading(false);
    },

    /**
     * Fetch user's shortlist from server
     * Updates shortlist with full vehicle data from ratebook
     */
    async vrpSearch_fetchUserShortlist() {
      // Early return if shortlist is empty - no point in fetching ratebook
      if (!this.shortlist || this.shortlist.length === 0) {
        return;
      }

      const filteredShortList = this.shortlist.filter((vehicle) => {
        return !vehicle.fuelType || !vehicle.vehicleName || !vehicle.vehicleDescription || !vehicle.consumerPrice;
      });
      if (filteredShortList.length === 0) {
        return;
      }
      const {
        results: { rows: ratebook, totalCount }
      } = await this.fetchRatebook({
        pair: undefined,
        vehicleMake: undefined,
        catalogVehicleId: this.shortlist?.map(({ catalogVehicleId }) => catalogVehicleId) || [],
        pageSize: 100,
        minDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM,
        maxDuration: this.leaseTerm?.value || DEFAULT_LEASE_TERM
      });

      this.shortlist = this.shortlist.map((shortlist) => {
        const vehicle = ratebook.find((vehicle) => {
          const matchId = vehicle.catalogVehicleId === shortlist.catalogVehicleId;

          // If leaseTerm has a value, require duration to match.
          // If leaseTerm is null/undefined, ignore duration filter.
          const matchLease = this.leaseTerm?.value ? vehicle.duration === this.leaseTerm.value : true;

          return matchId && matchLease;
        });

        if (vehicle) {
          vehicle.isShortlisted = true;
        }
        return vehicle;
      });
    },

    /**
     * Calculate total pages for pagination
     * @returns {Number} Total number of pages
     */
    vrpSearch_calculateTotalPages() {
      return Math.ceil(this.ratebook.resultCount / this.pageSize);
    }
  }
};
